import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Label,
  LoadingIndicator,
  Textarea,
  useToggle,
} from '@commenda/kakashi';
import { useState } from 'react';
import { useNotification } from '../../../hooks/useNotification';

interface IBulkArchiveFilingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCount: number;
  onArchiveMany: (reason: string) => Promise<boolean>;
  onSuccess: () => void;
}

export const BulkArchiveFilingsModal = ({
  isOpen,
  onClose,
  selectedCount,
  onArchiveMany,
  onSuccess,
}: IBulkArchiveFilingsModalProps) => {
  const { setNotification } = useNotification();
  const [reason, setReason] = useState('');
  const isSubmitting = useToggle(false);

  const handleArchive = async () => {
    isSubmitting.on();
    const success = await onArchiveMany(reason);

    if (!success) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to archive filings.',
      });
      isSubmitting.off();
      return;
    }

    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: 'Filings archived successfully.',
    });

    await onSuccess();
    isSubmitting.off();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[520px]">
        <DialogHeader className='flex flex-col items-start'>
          <DialogTitle>{`Archive ${selectedCount} ${selectedCount === 1 ? 'filing' : 'filings'}`}</DialogTitle>
          <DialogDescription>This action cannot be undone.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-xl p-4">
          <FieldWrapper>
            <Label htmlFor="bulk-archive-reason">Tell us why (optional)</Label>
            <Textarea
              id="bulk-archive-reason"
              placeholder="Detail out why you wish to archive these filings"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
            />
          </FieldWrapper>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting.visible}>
            Cancel
          </Button>
          <Button variant="destructive-primary" onClick={handleArchive} disabled={isSubmitting.visible}>
            {isSubmitting.visible ? (
              <div className="flex items-center gap-sm">
                <LoadingIndicator size={16} />
                Confirming
              </div>
            ) : (
              'Confirm'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkArchiveFilingsModal;


